/**
 * Tolerance utility functions
 * Handles tolerance options filtering and API seqno logic
 */

import { API_SEQNO } from 'src/constants/app';
import { MACHINING_OPTIONS } from 'src/constants/quotation/optionSelect';
import _ from 'lodash';

// ============================================
// Types
// ============================================

export type ToleranceActiveGroup = 'base' | 'api' | null;

export type ToleranceCodesInput = {
  thicknessCd?: string;
  widthCd?: string;
  lengthCd?: string;
  outsideDiaCd?: string;
};

// ============================================
// Helper Functions
// ============================================

/**
 * Check if apiSeqno belongs to base group (domestic/hakudo)
 * Base group: apiSeqno 0 (domestic) or 1 (hakudo)
 */
export const isBaseApiSeqnoGroup = (apiSeqno: number | undefined): boolean => {
  return apiSeqno === API_SEQNO.DOMESTIC || apiSeqno === API_SEQNO.HAKUDO;
};

/**
 * Find apiSeqno from tolerance code using options array
 */
export const findApiSeqnoFromToleranceCode = (
  options: Response.ToleranceOption[],
  toleranceCode: string,
): number | undefined => {
  if (!toleranceCode || !options) return undefined;
  const option = options?.find((opt) => opt?.value === toleranceCode);
  return option?.apiSeqno;
};

/**
 * Filter tolerance options by group (base or api)
 * - base group: apiSeqno 0, 1
 * - api group: other apiSeqno values
 */
export const filterOptionsByGroup = (
  options: Response.ToleranceOption[],
  activeGroup: ToleranceActiveGroup,
): Response.ToleranceOption[] => {
  if (!options || activeGroup === null) return options;
  return options.filter((opt) => {
    // Always include the empty/default option so user can deselect
    if (opt.value === '') return true;

    const isBase = isBaseApiSeqnoGroup(opt?.apiSeqno);
    return activeGroup === 'base' ? isBase : !isBase;
  });
};

/**
 * Get the preferred API seqno from selected tolerance codes.
 * Logic:
 * 1. Find apiSeqno for each selected tolerance code
 * 2. If any has apiSeqno === 0 or 1 (base group), return that apiSeqno
 * 3. If all are from API group (apiSeqno !== 0 && !== 1), compare priority
 *    from priceApiList and return the one with highest priority (smallest priority number)
 * 4. If no tolerance is selected, return undefined
 */
export const getApiSeqnoFromToleranceCodes = (
  toleranceCodes: ToleranceCodesInput,
  toleranceOptions: Response.ToleranceGroupOptions,
  priceApiList?: { seqno: number; priority?: number }[],
): number | undefined => {
  const mappings = [
    {
      code: toleranceCodes.thicknessCd,
      options: toleranceOptions?.thickness,
    },
    { code: toleranceCodes.widthCd, options: toleranceOptions?.width },
    { code: toleranceCodes.lengthCd, options: toleranceOptions?.length },
    {
      code: toleranceCodes.outsideDiaCd,
      options: toleranceOptions?.outsideDia,
    },
  ];

  const selectedApiSeqnos: number[] = [];

  for (const { code, options } of mappings) {
    if (code && options) {
      const apiSeqno = findApiSeqnoFromToleranceCode(options, code);
      if (apiSeqno !== undefined) {
        // If base group (0 or 1), return immediately
        if (isBaseApiSeqnoGroup(apiSeqno)) {
          return apiSeqno;
        }
        selectedApiSeqnos.push(apiSeqno);
      }
    }
  }

  // No tolerance selected
  if (selectedApiSeqnos.length === 0) return undefined;

  // All are from API group, find the one with highest priority
  if (!priceApiList || priceApiList.length === 0) {
    // If no priceApiList, return the first one
    return selectedApiSeqnos[0];
  }

  // Find the apiSeqno with highest priority (smallest priority number)
  let bestApiSeqno = selectedApiSeqnos[0];
  let bestPriority = Infinity;

  for (const apiSeqno of selectedApiSeqnos) {
    const apiInfo = priceApiList.find((api) => api.seqno === apiSeqno);
    if (apiInfo && apiInfo.priority !== undefined) {
      if (apiInfo.priority < bestPriority) {
        bestPriority = apiInfo.priority;
        bestApiSeqno = apiSeqno;
      }
    }
  }

  return bestApiSeqno;
};

/**
 * Render tolerance options from API response to dropdown format
 */
export const renderToleranceOptions = (
  list: Response.ToleranceEntity[],
): Response.ToleranceOption[] => {
  const listOptions = list?.map((item) => {
    return {
      label: item?.kakokosaTanka,
      value: item?.kakokosaCd,
      apiSeqno: item?.apiSeqno,
    };
  });
  return listOptions;
};

/**
 * Check and return machining options based on product type and API response
 */
export const checkMachiningOptions = (
  product: string,
  toleranceOptions?: Response.ToleranceOption[],
  apiCalled?: boolean,
): Response.ToleranceOption[] => {
  if (apiCalled) {
    if (toleranceOptions) {
      const defaultValue = { label: '', value: '' };
      if (!_.includes(toleranceOptions, defaultValue)) {
        toleranceOptions = _.concat([defaultValue], toleranceOptions);
      }
    }
    return toleranceOptions || MACHINING_OPTIONS;
  }
  return MACHINING_OPTIONS;
};
